import React, { useMemo, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { Calendar, Clock } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import './DateInput.css';

// DateInput — wraps react-datepicker with the app's dark + lime-accent
// design tokens. Drop-in replacement for <input type="date|datetime-local">:
//
//   <DateInput value={iso} onChange={setIso} />            // date-only
//   <DateInput value={iso} onChange={setIso} withTime />   // datetime-local
//
// Value format matches the native input exactly:
//   date           → "YYYY-MM-DD"
//   datetime-local → "YYYY-MM-DDTHH:MM"
//
// Native min/max props (also "YYYY-MM-DD[THH:MM]") pass through.

function parseValue(s, withTime) {
  if (!s) return null;
  // Native datetime-local strings are local time, no Z. Date.parse with a
  // plain "YYYY-MM-DDTHH:MM" treats it as local. Date-only strings get the
  // T00:00 suffix so they land on the local day, not UTC midnight (which
  // would shift to the previous day in negative-offset zones).
  const safe = withTime ? s : `${s}T00:00`;
  const d = new Date(safe);
  return Number.isNaN(d.valueOf()) ? null : d;
}

function formatValue(d, withTime) {
  if (!d || Number.isNaN(d.valueOf())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (!withTime) return ymd;
  return `${ymd}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Render the input ourselves so we can attach the same classes the native
// input had, plus the trailing Calendar/Clock icon. react-datepicker
// forwards focus + value via `customInput`.
const TriggerInput = forwardRef(function TriggerInput(
  { value, onClick, onChange, placeholder, withTime, disabled, required, id, name, className, ...rest },
  ref,
) {
  const Icon = withTime ? Clock : Calendar;
  return (
    <div className="relative">
      <input
        ref={ref}
        id={id}
        name={name}
        value={value || ''}
        onClick={onClick}
        onChange={onChange}
        onFocus={onClick}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        readOnly  /* the picker is the source of truth; typing is via the popup */
        className={className}
        {...rest}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={onClick}
        disabled={disabled}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-white/70 hover:text-white hover:bg-[#B0FC38]/20 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
        aria-label={withTime ? 'Open date and time picker' : 'Open date picker'}
      >
        <Icon className="w-4 h-4" />
      </button>
    </div>
  );
});

export default function DateInput({
  value,
  onChange,
  withTime = false,
  min,
  max,
  disabled,
  required,
  id,
  name,
  placeholder,
  className,
  // Optional shortcuts to make a date-only field default to a nice time
  // when the user later toggles into datetime. Not commonly used.
  ...rest
}) {
  const selected = useMemo(() => parseValue(value, withTime), [value, withTime]);
  const minDate = useMemo(() => parseValue(min, withTime), [min, withTime]);
  const maxDate = useMemo(() => parseValue(max, withTime), [max, withTime]);

  const handleChange = (d) => {
    onChange?.(formatValue(d, withTime));
  };

  return (
    <DatePicker
      selected={selected}
      onChange={handleChange}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      required={required}
      id={id}
      name={name}
      showTimeSelect={withTime}
      timeIntervals={15}
      timeCaption="Time"
      dateFormat={withTime ? 'MMM d, yyyy · h:mm aa' : 'MMM d, yyyy'}
      placeholderText={placeholder || (withTime ? 'Select date & time' : 'Select date')}
      // Popup positioning + portal for tight modal contexts
      popperPlacement="bottom-start"
      popperClassName="nexsav-datepicker-popper"
      calendarClassName="nexsav-datepicker-calendar"
      wrapperClassName="block w-full"
      autoComplete="off"
      customInput={
        <TriggerInput
          withTime={withTime}
          className={className}
          {...rest}
        />
      }
    />
  );
}
